import { useEffect, useCallback } from "react";
import { isMobileViewport } from "../utils/viewport.js";

export default function GoogleTranslate() {
  // Function to trigger translate dropdown
  const triggerTranslate = useCallback(() => {
    // Function to try opening the translate dropdown
    const tryOpenTranslate = () => {
      // Look for the Google Translate select element
      const translateSelect = document.querySelector(".goog-te-combo");
      
      if (translateSelect) {
        // Focus and click to open dropdown
        translateSelect.focus();
        translateSelect.click();
        
        // Also try dispatching events to ensure it opens
        const mouseDownEvent = new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        translateSelect.dispatchEvent(mouseDownEvent);
        
        const mouseUpEvent = new MouseEvent("mouseup", {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        translateSelect.dispatchEvent(mouseUpEvent);
        
        // Try keyboard event as well
        const keyDownEvent = new KeyboardEvent("keydown", {
          bubbles: true,
          cancelable: true,
          key: "ArrowDown",
          code: "ArrowDown",
        });
        translateSelect.dispatchEvent(keyDownEvent);
        
        return true;
      }
      
      // Alternative: try to find and click the translate button/icon
      const translateButton = document.querySelector("#google_translate_element a, .goog-te-banner-frame a");
      if (translateButton) {
        translateButton.click();
        return true;
      }
      
      return false;
    };

    // Try immediately
    if (!tryOpenTranslate()) {
      // If not found, wait and retry with increasing delays
      const retries = [300, 600, 1000, 1500];
      retries.forEach((delay, index) => {
        setTimeout(() => {
          if (!tryOpenTranslate() && index === retries.length - 1) {
            // Last retry - try one more time after a longer delay
            setTimeout(tryOpenTranslate, 2000);
          }
        }, delay);
      });
    }
  }, []);

  useEffect(() => {
    // Skip on mobile to prevent crashes (the shared phone breakpoint).
    if (typeof window === "undefined" || isMobileViewport()) {
      return;
    }

    // Load Google Translate script
    const scriptId = "google-translate-script";
    
    // Check if script already exists
    if (document.getElementById(scriptId)) {
      // Script already loaded, try to trigger translate
      setTimeout(() => {
        try {
          triggerTranslate();
        } catch (error) {
          console.error("Failed to trigger translate:", error);
        }
      }, 100);
      return;
    }

    // Create and load the script
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "text/javascript";
    script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    
    // Define the callback function with error handling
    window.googleTranslateElementInit = () => {
      try {
        if (!window.google || !window.google.translate) {
          return;
        }

        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,ja,ko,zh-CN,es,fr,de,pt,it,ru",
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
        
        // Auto-trigger translate dropdown after widget initializes
        setTimeout(() => {
          try {
            triggerTranslate();
          } catch (error) {
            console.error("Failed to trigger translate:", error);
          }
        }, 800);
      } catch (error) {
        console.error("Failed to initialize Google Translate:", error);
      }
    };

    script.onerror = () => {
      console.error("Failed to load Google Translate script");
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove script if component unmounts (optional)
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [triggerTranslate]);

  return <div id="google_translate_element" style={{ display: "none" }} />;
}

